import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AdminUser } from '@app/shared/services/user.service';
import { AwbShipmentRO, ReturnedDocumentDto, ShipmentsService } from '@app/shared/services/shipments.service';
import { DateFormats, DefaultTimezone, PanelWidths, ShippingProviders } from '@app/shared/constants';
import { environment } from '../../../../../../../environments/environment';
import { DocumentTypes } from '@app/pages/authenticated/components/shipment-details/shipment-details.component';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import * as FileSaver from 'file-saver';
import { HelpersService } from '@app/shared/services/helpers.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { ViewDocumentComponent } from '@app/pages/authenticated/components/view-document/view-document.component';
import { MtxDrawer } from '@ng-matero/extensions/drawer';
import { Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { UpdateAwbComponent } from '../update-awb/update-awb.component';
import { TrackingComponent } from '@app/pages/authenticated/components/tracking/tracking.component';

@Component({
  standalone: false,
  selector: 'awb-listing',
  templateUrl: './awb-listing.component.html',
})
export class AwbListingComponent implements OnInit{
  @Input() user: AdminUser;
  @Input() awbs: AwbShipmentRO[];
  @Input() canEditAwb: boolean = false;

  @Output() refreshAwbs: EventEmitter<void> = new EventEmitter();

  public loading: boolean = false;
  public gettingAWB: boolean = false;
  public openingModal: boolean = false;
  public showingTracking: boolean = false;
  public DATE_FORMAT: string = DateFormats.DATE_SHORT;
  public CDN_URL: string = environment.CDN_URL;
  public displayedColumns: string[] = ['shipmentCreatedAt', 'awbNumber', 'lastMileProvider', 'destinationAirport', 'actions'];
  public sortedAWbs: AwbShipmentRO[] = [];
  public defaultTimezone = DefaultTimezone;

  constructor(
    private helpersService: HelpersService,
    private shipmentsService: ShipmentsService,
    private bannerNotificationsService: BannerNotificationsService,
    private logger: LoggerService,
    private dialog: MatDialog,
    private drawer: MtxDrawer,
  ) {}

  ngOnInit() {
    this.sortedAWbs = this.awbs;
  }

  public sortData(sort: Sort) {
    const data = [...this.awbs];
    if (!sort.active || sort.direction === '') {
      this.sortedAWbs = [...data];
      return;
    }

    this.sortedAWbs = [...data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'awbNumber':
          return this.helpersService.compare(a.awbNumber, b.awbNumber, isAsc);
        case 'lastMileProvider':
          return this.helpersService.compare(a.lastMileProvider, b.lastMileProvider, isAsc);
        case 'destinationAirport':
          return this.helpersService.compare(a.destinationAirport, b.destinationAirport, isAsc);
        default:
          return 0;
      }
    })];
  }

  public downloadAwb(awb: AwbShipmentRO, showPreview: boolean = false): void {
    if (!this.gettingAWB) {
      this.gettingAWB = true;
      this.openingModal = false;
      const query = {
        provider: ShippingProviders.FREIGHT_AIR_CANADA,
        type: DocumentTypes.LABELS,
        id: awb.awbNumber,
        trackingNumber: awb.awbNumber,
        returnTrackingNumber: undefined,
      };

      this.shipmentsService.getDocuments(query).subscribe(
        (res: ReturnedDocumentDto) => {
          this.logger.log('GET Shipment documents', res);

          const mimeType = this.shipmentsService.getMimeType(res.extension);
          const trackingNumber = awb.awbNumber || '';
          const localString = awb.destinationAirport === awb.originAirport ? '-LOCAL' : '';
          const downloadFileName = `${awb.originAirport}-${awb.destinationAirport}-${awb.lastMileProvider.toUpperCase()}${localString}-${trackingNumber}-${awb.shipDate}.${res.extension}`;

          if (showPreview) {
            if (!this.openingModal) {
              this.openingModal = true;
              this.drawer.open(ViewDocumentComponent, {
                width: this.helpersService.getDrawerWidth(),
                disableClose: true,
                closeOnNavigation: false,
                data: {
                  document: res.fileData,
                  downloadFileName,
                  previewTitle: 'View Invoice',
                },
              });
              this.openingModal = false;
            }
          } else {
            const blob = this.helpersService.b64toBlob(res.fileData, mimeType);
            FileSaver.saveAs(blob, downloadFileName);
          }

          this.gettingAWB = false;
        },
        (err: any) => {
          this.bannerNotificationsService.error('Could not get shipment document', {
            category: 'GET shipment document error',
            message: err,
          });
          this.gettingAWB = false;
        }
      );
    }
  }

  public update(awb: AwbShipmentRO): void {
    const drawerRef = this.drawer.open(UpdateAwbComponent, {
      width: this.helpersService.getDrawerWidth(),
      disableClose: true,
      closeOnNavigation: false,
      data: { awb, adminUser: this.user },
    });

    drawerRef.afterDismissed().subscribe((result) => {
      if (result) {
        // emit a refresh event
        this.refreshAwbs.emit();
      }
    });
  }

  public openTrackingFromAWB(awb: AwbShipmentRO): void {
    if (this.showingTracking) {
      return;
    }

    this.showingTracking = true;
    const queryParams = {
      tracking_number: awb.awbNumber,
      include_admin_charge_details: true,
    };

    this.shipmentsService.findByTracking(queryParams).subscribe(
      (shipment: any) => {
        if (!shipment) {
          this.bannerNotificationsService.error('Could not find shipment for this AWB number', {
            category: 'Tracking error',
            message: 'No shipment found',
          });
          this.showingTracking = false;
          return;
        }

        if (!shipment.provider || !shipment.companyAndUserInfo?.company) {
          this.bannerNotificationsService.error('Shipment data is incomplete', {
            category: 'Tracking error',
            message: 'Missing required shipment information',
          });
          this.showingTracking = false;
          return;
        }

        const drawerRef = this.drawer.open(TrackingComponent, {
          width: PanelWidths.desktopFull,
          disableClose: true,
          closeOnNavigation: false,
          data: {
            shipment: shipment,
            trackingNumber: shipment.tracking,
            provider: shipment.provider,
            company: shipment.companyAndUserInfo.company,
            shouldUpdateTrackingStatus: true,
          },
        });

        drawerRef.afterDismissed().subscribe(() => {
          this.showingTracking = false;
        });
      },
      (err: Error) => {
        this.bannerNotificationsService.error('Could not load tracking information', {
          category: 'Tracking error',
          message: err,
        });
        this.showingTracking = false;
      }
    );
  }
}
